<template>
  <!-- <img src="@/assets/img/community_banner.jpg" class="w-full max-h-[25rem] min-h-[11.25rem] object-cover" /> -->
  <BoardHeader />

  <main class="w-full max-w-[82.75rem] mx-auto lg:mt-12 px-4 sm:px-6 lg:px-8">
    <header class="flex flex-col items-center justify-center mb-8">
      <h1 class="text-3xl font-bold text-gray-900 mb-2">{{ pageTitle }}</h1>
      <p class="text-gray-500">다양한 소식과 정보를 확인하세요.</p>
    </header>

    <!-- Board Navigation Tabs -->
    <div class="mb-10 border-b border-gray-200">
      <nav class="-mb-px flex md:justify-center space-x-8 overflow-x-auto pb-0 max-md:px-0" aria-label="Tabs">
        <router-link
          v-for="board in boardTitles"
          :key="board.id"
          :to="`/board/${board.id}`"
          class="whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium transition-colors duration-200"
          :class="[
            $route.path.includes(`/board/${board.id}`)
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
          ]"
        >
          {{ board.name }}
        </router-link>
      </nav>
    </div>

    <!-- Search & Filter -->
    <div class="mb-12">
      <div class="flex flex-col items-center gap-6">

        <!-- Search Bar -->
        <div class="flex w-full max-w-2xl items-center gap-0 rounded-lg border border-gray-300 bg-white overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent shadow-sm">
          <select v-model="criteria" class="h-12 border-none bg-transparent pl-4 pr-8 text-sm text-gray-600 focus:ring-0">
            <option value="all">제목+내용</option>
            <option value="title">제목</option>
            <option value="content">본문</option>
          </select>
          <div class="h-6 w-px bg-gray-200"></div>
          <input 
            type="text" 
            v-model="keyword" 
            @keyup.enter="searchPostList" 
            placeholder="검색어를 입력하세요" 
            class="h-12 flex-grow border-none bg-transparent px-4 text-sm text-gray-900 placeholder-gray-400 focus:ring-0" 
          />
          <button type="button" @click="searchPostList" class="h-12 px-6 bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors">
            검색
          </button>
        </div>
      </div>
    </div>

    <div class="mb-6 flex justify-between items-center">
      <h3 class="text-xl font-bold text-gray-800 flex items-center gap-2">
        <span class="w-1 h-6 bg-blue-600 rounded-full inline-block"></span>
        {{ pageTitle }}
      </h3>
      <!-- Write Button -->
      <router-link :to="`write/${boardId}`" class="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors shadow-sm">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
        글 작성
      </router-link>
    </div>

    <!-- Webzine View -->
    <div class="grid md:grid-cols-4 sm:grid-cols-2 gap-6 mb-10">
      <div v-for="post in postList" :key="post.postId" class="group">

        <router-link :to="'/board/' + post.boardId + '/' + post.postId" class="block h-full bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow border border-gray-100">
          <div class="relative aspect-[7/10] overflow-hidden bg-gray-100">
            <img
              v-if="post.postThumbnail"
              :src="'https://eduwind-api.nodeblack.net/' + post.postThumbnail"
              :alt="post.postTitle"
              class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div v-else class="w-full h-full flex items-center justify-center text-gray-300 bg-gray-50">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1"
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div v-if="isNew(post.createdDateTime)" class="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded shadow-sm">NEW</div>
          </div>
          <div class="p-4">
            <h4 class="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">{{ post.postTitle }}</h4>
            <div class="flex items-center justify-between text-xs text-gray-500 mt-auto">
              <span>{{ post.postAuthor }}</span>
              <span>{{ formDate2(post.createdDateTime) }}</span>
            </div>
            <div class="flex flex-wrap gap-1 mt-auto pt-2" v-if="post.postTags && post.postTags.length > 0">
              <span v-for="tag in post.postTags" :key="tag" class="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                #{{ tag }}
              </span>
            </div>
          </div>
        </router-link>
      </div>
    </div>

    <!-- No Posts Message -->
    <div v-if="postList.length === 0" class="w-full flex flex-col items-center justify-center py-20 h-[calc(300px)]">
      <div class="flex flex-col items-center justify-center gap-4">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-20 w-20 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p class="text-2xl font-bold text-gray-500">등록된 게시글이 없습니다.</p>
      </div>
    </div>

    <!-- List View -->
    <!--
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-10" v-else>
      <table class="w-full text-sm text-left text-gray-500">
        <thead class="text-xs text-gray-700 uppercase bg-gray-50 border-b border-gray-200">
          <tr>
            <th scope="col" class="px-6 py-4 text-center w-20 font-semibold">No.</th>
            <th scope="col" class="px-6 py-4 font-semibold">제목</th>
            <th scope="col" class="px-6 py-4 text-center w-32 hidden md:table-cell font-semibold">작성자</th>
            <th scope="col" class="px-6 py-4 text-center w-36 hidden md:table-cell font-semibold">작성일</th>
            <th scope="col" class="px-6 py-4 text-center w-24 hidden lg:table-cell font-semibold">조회수</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr v-for="post in postList" :key="post.postId" class="hover:bg-gray-50 transition-colors" v-if="postList.length > 0">
            <td class="px-6 py-4 text-center font-medium text-gray-900">
              <div v-if="post.seq === null" class="inline-flex justify-center items-center w-6 h-6 bg-red-100 text-red-600 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
              </div>
              <span v-else>{{ post.seq }}</span>
            </td>
            <td class="px-6 py-4">
              <router-link :to="'/board/' + post.boardId + '/' + post.postId" class="group flex items-center gap-2">
                <span v-if="post.isSecret" class="text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </span>
                <span class="text-gray-900 font-medium group-hover:text-blue-600 transition-colors line-clamp-1">{{ post.postTitle }}</span>

                <span v-if="post.commentCount > 0" class="inline-flex items-center gap-1 text-xs text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded-md">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fill-rule="evenodd"
                      d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  {{ post.commentCount }}
                </span>

                <span v-if="isNew(post.createdDateTime)" class="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full">N</span>
              </router-link>
            </td>
            <td class="px-6 py-4 text-center hidden md:table-cell text-gray-600">{{ post.postAuthor }}</td>
            <td class="px-6 py-4 text-center hidden md:table-cell text-gray-500">{{ formDate2(post.createdDateTime) }}</td>
            <td class="px-6 py-4 text-center hidden lg:table-cell text-gray-500">{{ post.postHit }}</td>
          </tr>
          <tr v-if="!postList || postList.length === 0">
            <td colspan="5" class="px-6 py-12 text-center text-gray-500 bg-gray-50">
              <div class="flex flex-col items-center justify-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p>등록된 게시글이 없습니다.</p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    -->

    <pagination v-if="pageInfo.page && postList.length > 0" :pageInfo="pageInfo" :page="page" :size="size" @updatePage="updatePage" class="mb-16" />
  </main>
</template>

<script setup>
import { ref, computed, onMounted, watchEffect } from "vue";
import axios from "axios";
import Pagination from "@/components/Pagination.vue";
import { formDate2 } from "@/assets/js/util.js";
import { useRoute } from "vue-router";
import BoardHeader from "./components/boardHeader.vue";

const route = useRoute();

const pageInfo = ref({});
const postList = ref({});

const boardTitles = ref([
  { id: 5, name: "영유아부" },
  { id: 6, name: "유치부" },
  { id: 7, name: "유년부" },
  { id: 8, name: "초등부" },
  { id: 9, name: "청소년부" },
  { id: 10, name: "대학청년부" },
  { id: 11, name: "구역/장년" },
]);

const pageTitle = computed(() => {
  if (searching.value) {
    return "검색 결과";
  } else {
    const currentPath = route.path;
    return boardTitles.value.find((board) => board.id === parseInt(currentPath.split("/")[2])).name;
  }
});

const boardId = ref(route.params.boardId || 5);
const page = ref(1);
const size = ref(12);

const criteria = ref("all");
const keyword = ref("");
const selectedBoardId = ref("");

const searching = ref(false); // 검색함 플래그

// 게시글 목록 조회
const getPostList = async () => {
  const boardIdValue = boardId.value;
  searching.value = false;

  try {
    const res = await axios.get(`/new_api/post/list?page=${page.value}&size=${size.value}&boardId=${boardIdValue}`);
    pageInfo.value = res.data.data.pageInfo;
    postList.value = res.data.data.postList.filter((post) => !post.isPin);
  } catch (err) {
    console.error(err);
  }
};

const searchPostList = async () => {
  const boardIdValue = selectedBoardId.value;
  searching.value = true;

  try {
    const res = await axios.get(`/new_api/post/list?page=1&size=${size.value}&boardId=${boardIdValue}&criteria=${criteria.value}&keyword=${keyword.value}`);

    pageInfo.value = res.data.data.pageInfo;
    postList.value = res.data.data.postList;
  } catch (err) {
    console.error(err);
  }
};

const updatePage = (num) => {
  page.value = num;
  getPostList();
};

onMounted(async () => {
  boardId.value = route.params.boardId || 5;
  await getPostList();
});

watchEffect(() => {
  getPostList();
});

const isNew = (date) => {
  const today = new Date();
  today.setHours(today.getHours() - 24);
  return new Date(date) > today;
};
</script>
